<script setup>
import { ref, watch, inject } from 'vue';
import ModaleEditionMiseEnForme from '@/components/modales/ModaleEditionMiseEnForme.vue';
import ModaleEditionDecalage from '@/components/modales/ModaleEditionDecalage.vue';
import ModaleAjoutObjet from '@/components/modales/ModaleAjoutObjet.vue';
import {
	getMiseEnFormeElement,
    getPositionBriqueRapport,
	getBlocEntetesTableau,
	getBlocEntetesObjetsTableau,
	getEnteteRegroupementTableau,
	trouverPremiereCelluleTableau,
	trouverPremiereCelluleTableauVerifiant } from '@/scripts/global.js';
import { 
	apiAjouterElementRapport,
	apiMettreEnFormeElements,
	apiModifierMasquageBriquesRapport,
	apiModifierLargeurElementRapport,
	apiModifierDecalageElementRapport,
	apiModifierAlignementElementRapport,
	apiTransposerElementsRapport,
	apiAjouterElementTableau,
	apiEditerFiltreBlocEntetesTableau,
	apiBasculerAffichageTotauxBlocEntetesTableau,
	apiBasculerAffichageSousTotauxRegroupementTableau,
	apiModifierDecalageBlocEntetesTableau,
	apiSupprimerBlocEntetesTableau,
	apiModifierTypeGraphique,
	apiAjouterSerieGraphique,
	apiAjouterObjet } from '@/services/ServiceModele.js';

const { element } = defineProps({
	element: Object
});

const elementsSelectionnes = inject('elementsSelectionnes');
const appelerApiModifModele = inject('appelerApiModifModele');
const getTypeObjet = inject('getTypeObjet');
const getMisesEnForme = inject('getMisesEnForme');
const copierElements = inject('copierElements');
const couperElements = inject('couperElements');
const collerElements = inject('collerElements');
const supprimerElementsSelectionnes = inject('supprimerElementsSelectionnes');
const donnerFocusAuRapport = inject('donnerFocusAuRapport');
const selectionnerElement = inject('selectionnerElement');
const trouverElementEtParent = inject('trouverElementEtParent');
const editerExpression = inject('editerExpression');
const afficherBarreFlottante = inject('afficherBarreFlottante');

const boutonActions = ref(null);
const affichageMenu = ref(false);
const affichageModaleEditionMiseEnForme = ref(false);
const affichageModaleSaisieDecalage = ref(false);
const affichageModaleAjoutObjet = ref(false);

function basculerAffichageBarreFlottante() {
	affichageMenu.value = !affichageMenu.value;
}

watch(() => element.ouvertureMenu, (ouvMenu) => {
	if (ouvMenu) {
		delete element.ouvertureMenu;
		const items = getItemsMenuActions();
		let itemsCourants = items;
		let dernierItem = null;
		const selection = [];
		for (const idItem of ouvMenu) {
			let indiceItem = itemsCourants.findIndex(item => item.id === idItem);
			if (indiceItem === -1) {
				return;
			}
			selection.push(indiceItem);
			dernierItem = itemsCourants[indiceItem];
			itemsCourants = dernierItem.items;
		}
		if (itemsCourants) {
			afficherBarreFlottante({
				items: items, 
				selectionItems: selection,
				composantParent: boutonActions.value,
				quandMasquage: () => donnerFocusAuRapport()
			});
		} else if (dernierItem) {
			dernierItem.commande.apply();
		}
	}
});

watch(() => affichageMenu.value, (affichage) => {
	if (affichage) {
		afficherBarreFlottante({
			items: getItemsMenuActions(), 
			selectionItems: [],
			composantParent: boutonActions.value,
			quandMasquage: () => donnerFocusAuRapport()
		});
	} else {
		afficherBarreFlottante(null);
		donnerFocusAuRapport();
	}
});

function getItemsMenuActions() {
	if (elementsSelectionnes.value.length > 0) {
		const itemsMenu = getItemsMenuActionsElement(elementsSelectionnes.value[0]);
		for (let i = 1; i < elementsSelectionnes.value.length; i++) {
			let itemsMenuElement = getItemsMenuActionsElement(elementsSelectionnes.value[i]);
			fusionnerItemsMenuActions(itemsMenu, itemsMenuElement);
		}
		return itemsMenu;
	} else {
		return [];
	}
}

function fusionnerItemsMenuActions(itemsMenuAgreges, itemsMenu) {
	const indiceItemsASupprimer = [];
	for (const itemMenu of itemsMenuAgreges) {
		if (itemMenu.multiple || itemMenu.commande == null) {
			let indiceItem = itemsMenu.map(item => item.id).indexOf(itemMenu.id);
			if (indiceItem != -1) {
				if (itemMenu.items != null) {
					fusionnerItemsMenuActions(itemMenu.items, itemsMenu[indiceItem].items);				
				}
			} else {
				indiceItemsASupprimer.push(itemsMenuAgreges.indexOf(itemMenu));
			}
		} else {
			indiceItemsASupprimer.push(itemsMenuAgreges.indexOf(itemMenu));
		}
	}
	indiceItemsASupprimer.reverse().forEach(indiceItem => itemsMenuAgreges.splice(indiceItem, 1));
}

function getItemsMenuActionsElement(element) {
	const itemsMenu = [];

	// ********************
	// Actions sur élément de rapport
	// ********************
	if (['SECTION', 'ZONE', 'TEXTE', 'TABLEAU', 'GRAPHIQUE', 'SELECTEUR'].includes(element.type)) {
		// Ajout d'élements de rapport
		// Ajout avant
		itemsMenu.push({
			id: 'ajout_avant',
			libelle: 'Ajouter avant',
			raccourci: '<em>Ctrl</em> + <em>-</em>',
			icones: ['fa fa-plus', 'fa fa-arrow-left icone-exposant'],
			groupe: 2,
			items: getItemsAjoutElementRapport(element, 'avant')
		});	
		
		// Ajout à zone
		if (element.type === 'ZONE') {
			itemsMenu.push({
				id: 'ajout_a_zone',
				libelle: 'Ajouter à la zone',
				icones: ['fa fa-plus', 'fa fa-arrow-down icone-exposant'],
				groupe: 2,
				items: getItemsAjoutElementRapport(element, 'zone')
			});	
		}

		// Ajout après
		itemsMenu.push({
			id: 'ajout_apres',
			libelle: 'Ajouter après',
			icones: ['fa fa-plus', 'fa fa-arrow-right icone-exposant'],
			raccourci: '<em>Ctrl</em> + <em>+</em>',
			groupe: 2,
			items: getItemsAjoutElementRapport(element, 'apres')
		});	

		itemsMenu.push({
			libelle: 'Position et dimensions',
			icones: ['fa fa-arrows-up-down-left-right'],
			groupe: 1,
			items: [
				{
					libelle: 'Largeur',
					icones: ['fa fa-arrows-left-right-to-line'],
					items: getItemsMenuLargeur(element)
				},
				{
					id: 'decalage_element_rapport',
					libelle: 'Décalage',
					icones: ['fa fa-arrow-right-from-bracket'],
					multiple: false,
					items: getItemsMenuDecalageElementRapport(element)
				},
				{
					libelle: 'Alignement',
					icones: ['fa fa-align-justify'],
					items: getItemsMenuAlignement(element)
				}
			]
		});	

		// Transposition
		if (['ZONE', 'TABLEAU'].includes(element.type)) {
			itemsMenu.push({
				id: 'transpose',
				libelle: 'Transposer',
				icones: ['fa fa-retweet'],
				groupe: 1,
				multiple: true,
				commande: () => transposerElement(element)
			});	
		}

	}

	// ********************
	// Actions sur cellule de tableau
	// ********************

	if (element.elementParent?.type === 'TABLEAU') {
		if (element.type != 'FiltreTableau') {
			// Actions sur bloc d'entêtes objets
			const itemsBlocsObjets = getItemsBlocsObjets(element);
			if (itemsBlocsObjets?.length) {
				itemsMenu.push({
					id: 'bloc_objets',
					libelle: 'Bloc d\'objets',
					icones: [`fa fa-table-columns ${element.elementParent.expressionsEnColonnes ? 'rotation-gauche' : ''}`],
					groupe: 2,
					items: itemsBlocsObjets
				});
			}

			// Actions sur bloc d'entêtes expressions
			const itemsBlocsExpressions = getItemsBlocsExpressions(element);
			if (itemsBlocsExpressions?.length) {
				itemsMenu.push({
					id: 'bloc_expressions',
					libelle: 'Bloc d\'expressions',
					icones: [`fa fa-table-columns ${!element.elementParent.expressionsEnColonnes ? 'rotation-gauche' : ''}`],
					groupe: 2,
					items: itemsBlocsExpressions
				});
			}

			// Actions sur entête de variable
			const itemsEntetesVariable = getItemsEntetesVariable(element, itemsBlocsObjets?.length);
			if (itemsEntetesVariable?.length) {
				itemsMenu.push({
					id: 'variables',
					libelle: 'Variables',
					icones: ['fa fa-ruler-horizontal'],
					groupe: 2,
					items: itemsEntetesVariable
				});
			}
			
			// Actions sur entête de regroupement
			const itemsEntetesRegroupement = getItemsEntetesRegroupement(element, itemsBlocsObjets?.length);
			if (itemsEntetesRegroupement?.length) {
				itemsMenu.push({
					id: 'regroupements',
					libelle: 'Regroupements',
					icones: ['fa fa-object-group'],
					groupe: 2,
					items: itemsEntetesRegroupement
				});
			}
			
			// Affichage des totaux
			const itemAffichageTotaux = getItemAffichageTotaux(element);
			if (itemAffichageTotaux != null) {
				itemsMenu.push(itemAffichageTotaux);
			}

			// Ajout rapide d'entête d'expression
			if (['EnteteExpression', 'ValeurExpression'].includes(element.type)) {
				let classeRotation = !element.elementParent.expressionsEnColonnes ? 'rotation-droite' : '';
				let ligneOuColonne = element.elementParent.expressionsEnColonnes ? 'colonne' : 'ligne';
				itemsMenu.push({
					id: `ajout_avant_entete_expr`,
					libelle: `Ajouter une ${ligneOuColonne} d'expression avant`,
					icones: [`fa fa-arrow-left icone-exposant  ${classeRotation}`, 'fa fa-calculator', 'fa fa-plus icone-indice'],
					multiple: false,
					groupe: 3,
					commande: () => {
						let indices = element.position.slice();
						ajouterEnteteExpression(element.elementParent, indices);
					}
				});
				itemsMenu.push({
					id: `ajout_apres_entete_expr`,
					libelle: `Ajouter une ${ligneOuColonne} d'expression après`,
					icones: [`fa fa-arrow-right icone-exposant  ${classeRotation}`, 'fa fa-calculator', 'fa fa-plus icone-indice'],
					multiple: false,
					groupe: 3,
					commande: () => {
						let indices = element.position.slice();
						indices[2]++;
						ajouterEnteteExpression(element.elementParent, indices);
					}
				});
			}


			// Ajout d'objets
			if (['EnteteObjetsBloc', 'EnteteObjet'].includes(element.type) && getBlocEntetesTableau(element, element.elementParent).idTypeObjetAjoutable != null) {
				itemsMenu.push({
					id: 'ajout_objet_depuis_tableau',
					libelle: 'Ajouter un objet',
					icones: ['fa fa-chess-knight', 'fa fa-plus icone-exposant'],
					groupe: 4,
					multiple: false,
					commande: () => {
						affichageModaleAjoutObjet.value = true;
					}
				});
			}
		}
		// Filtres
		const itemFiltres = getItemFiltres(element);
		if (itemFiltres != null) {
			itemsMenu.push(itemFiltres);
		}
	}
	
	// **********************************************
	// Actions sur graphique et cellules de graphique
	// **********************************************
	if (element.type === 'GRAPHIQUE') {
		// Type de graphique
		itemsMenu.push({
			libelle: 'Type de graphique',
			icones: ['fa fa-chart-line'],
			groupe: 1,
			items: [
				{
					id: 'modif_type_graphique_courbe',
					libelle: 'Courbe',
					icones: ['fa fa-chart-line'],
					multiple: false,
					commande: () => modifierTypeGraphique(element, 'COURBE')
				},
				{
					id: 'modif_type_graphique_histogramme',
					libelle: 'Histogramme',
					icones: ['fa fa-chart-simple'],
					multiple: false,
					commande: () => modifierTypeGraphique(element, 'HISTOGRAMME')
				},
				{
					id: 'modif_type_graphique_circulaire',
					libelle: 'Camembert',
					icones: ['fa fa-chart-pie'],
					multiple: false,
					commande: () => modifierTypeGraphique(element, 'CIRCULAIRE')
				},
				{
					id: 'modif_type_graphique_doughnut',
					libelle: 'Doughnut',
					icones: ['fa fa-circle-dot'],
					multiple: false,
					commande: () => modifierTypeGraphique(element, 'DOUGHNUT')
				},
				{
					id: 'modif_type_graphique_radar',
					libelle: 'Radar',
					icones: ['fa fa-circle-nodes'],
					multiple: false,
					commande: () => modifierTypeGraphique(element, 'RADAR')
				}
			]
		}); 
	}

	if (element.elementParent?.type === 'GRAPHIQUE') {
		if (['SerieGraphique', 'ValeurSerie'].includes(element.type)) {
			itemsMenu.push({
				id: 'ajout_serie_avant',
				libelle: 'Ajouter une série avant',
				icones: ['fa fa-arrow-up icone-exposant', 'fa fa-arrow-trend-up', 'fa fa-plus icone-indice'],
				groupe: 2,
				multiple: false,
				commande: () => {
					insererSerie(element.elementParent, element.indiceSerie);
				}
			});
		}
		itemsMenu.push({
			id: 'ajout_apres_serie',
			libelle: 'Ajouter une série après',
			icones: ['fa fa-arrow-down icone-exposant', 'fa fa-arrow-trend-up', 'fa fa-plus icone-indice'],
			groupe: 2,
			multiple: false,
			commande: () => {
				if (['AxePrincipal', 'ValeurAxe'].includes(element.type)) {
					insererSerie(element.elementParent, 0);
				} else if (['SerieGraphique', 'ValeurSerie'].includes(element.type)) {
					insererSerie(element.elementParent, element.indiceSerie + 1);
				}
			}
		});
	}

	// ********************
	// Actions globales
	// ********************
	if (element.type == 'FiltreTableau') {
		return itemsMenu;
	}

	// Mise en forme
	if (element.elementParent?.type !== 'GRAPHIQUE') {
		let mef = getMisesEnForme(element.type);
		itemsMenu.push({
			id: `modif_mef_${JSON.stringify(mef)}`, 
			libelle: 'Mise en forme',
			icones: ['fa fa-paint-roller'],
			groupe: 1,
			items: getItemsMenuMiseEnForme(mef)
		});
	}

	// Masquage
	if (element.masque) {
		itemsMenu.push({
			id: 'rendre_visible',
			libelle: 'Rendre visible',
			icones: ['fa fa-eye'],
			groupe: 6,
			multiple: true,
			commande: () => modifierMasquageBriquesRapport()
		});
	} else {
		itemsMenu.push({
			id: 'masquer',
			libelle: 'Masquer',
			icones: ['fa fa-eye-slash'],
			groupe: 6,
			multiple: true,
			commande: () => modifierMasquageBriquesRapport()
		});
	}
	
	// Editer
	itemsMenu.push({
		id: 'edition',
		libelle: 'Editer',
		icones: ['fa fa-edit'],
		groupe: 6,
		style: 'color: green; ',
		multiple: false,
		commande: () => activerModeEdition
	});

	// Couper
	itemsMenu.push({
		id: 'coupe',
		libelle: 'Couper',
		raccourci: '<em>Ctrl</em> + <em>x</em>',
		icones: ['fa fa-scissors'],
		groupe: 5,
		multiple: true,
		commande: () => couperElements()
	});

	// Copier
	itemsMenu.push({
		id: 'copie',
		libelle: 'Copier',
		raccourci: '<em>Ctrl</em> + <em>c</em>',
		icones: ['fa fa-copy'],
		groupe: 5,
		multiple: true,
		commande: () => copierElements()
	});

	// Coller
	itemsMenu.push({
		id: 'colle',
		libelle: 'Coller',
		raccourci: '<em>Ctrl</em> + <em>v</em>',
		icones: ['fa fa-paste'],
		groupe: 5,
		disabled: !navigator.clipboard.readText(),
		multiple: true,
		commande: () => collerElements(element)
	});

	// Suppression de brique de rapport
	itemsMenu.push({
		id: 'suppression',
		libelle: 'Supprimer',
		icones: ['fa fa-trash'],
		raccourci: '<em>Suppr</em>',
		groupe: 6,
		style: 'color: var(--couleur-supprimer); ',
		multiple: true,
		commande: () => supprimerElementsSelectionnes()
	});

	return itemsMenu;
}

function getItemsAjoutElementRapport(element, typeAjout) {
	const items = [];
	// Ajout des éléments simples : texte, tableau, graphique...
	if (element.type !== 'SECTION' || typeAjout === 'avant' && element.positionSection[element.positionSection.length - 1] === 0) {
		let positionSection;
		let positionNouvelElement;
		if (element.type === 'SECTION') {
			positionSection = element.positionSection.slice(0, element.positionSection.length - 1);
			positionNouvelElement = element.positionSection.slice();
			const sectionParente = element.elementParent;
			positionNouvelElement = [sectionParente.elements.length];
		} else {
			positionSection = element.positionSection;
			positionNouvelElement = element.positionDansSection.slice();
			if (typeAjout === 'zone') {
				positionNouvelElement.push(element.elements.length);
			} else if (typeAjout === 'apres') {
				positionNouvelElement[positionNouvelElement.length - 1]++;
			}
		}
		items.push({
			id: `ajout_${typeAjout}_texte`,
			libelle: 'Texte',
			icones: ['fa fa-font'],
			multiple: false,
			commande: () => ajouterElementRapport(positionSection, positionNouvelElement, 'TEXTE')
		});
		items.push({
			id: `ajout_${typeAjout}_tableau`,
			libelle: 'Tableau',
			icones: ['fa fa-table'],
			multiple: false,
			commande: () => ajouterElementRapport(positionSection, positionNouvelElement, 'TABLEAU')
		});
		items.push({
			id: `ajout_${typeAjout}_graphique`,
			libelle: 'Graphique',
			icones: ['fa fa-chart-line'],
			multiple: false,
			commande: () => ajouterElementRapport(positionSection, positionNouvelElement, 'GRAPHIQUE')
		});
		items.push({
			id: `ajout_${typeAjout}_selecteur`,
			libelle: 'Sélecteur',
			icones: ['fa fa-crosshairs'],
			multiple: false,
			commande: () => ajouterElementRapport(positionSection, positionNouvelElement, 'SELECTEUR')
		});
		items.push({
			id: `ajout_${typeAjout}_zone`,
			libelle: 'Sous-zone',
			icones: ['fa fa-square'],
			multiple: false,
			commande: () => ajouterElementRapport(positionSection, positionNouvelElement, 'ZONE')
		});

	}
	if (element.type === 'SECTION' || typeAjout === 'apres' && element.positionDansSection[element.positionDansSection.length - 1] === element.elementParent.elements.length - 1) {
		let positionSection;
		let positionNouvelleSection;
		if (element.type === 'SECTION') {
			positionSection = element.positionSection.slice();
			positionNouvelleSection = [positionSection.pop()];
			if (typeAjout === 'apres') {
				positionNouvelleSection[0]++;
			}
		} else {
			positionSection = element.positionSection;
			positionNouvelleSection = [0];
		}
		items.push({
			id: `ajout_${typeAjout}_sous_section`,
			libelle: 'Sous-section',
			icones: ['fa fa-list-ol'],
			multiple: false,
			commande: () => ajouterElementRapport(positionSection, positionNouvelleSection, 'SECTION')
		});
	}
	return items;
}

function getItemsBlocsObjets(element) {
	if (['EnteteObjetsBloc', 'EnteteObjet', 'EnteteRegroupement', 'EnteteValeurGroupe', 'EnteteAgregatGroupe', 'EnteteAgregatTotal'].includes(element.type) || ['EnteteVariable', 'EnteteValeurVariable'].includes(element.type) && element.position.length === 2) {
		const classeRotation = element.elementParent.expressionsEnColonnes ? 'rotation-droite' : '';
		return getItemsBlocs(element, [element.position[0], element.position[1]], classeRotation, 'BlocEntetesObjets');
	}
}

function getItemsBlocsExpressions(element) {
	if (element.type === 'EnteteExpression' || element.type === 'EnteteValeurVariable' && element.position.length === 3) {
		const classeRotation = !element.elementParent.expressionsEnColonnes ? 'rotation-droite' : '';
		return getItemsBlocs(element, [element.position[0], element.position[1]], classeRotation, 'BlocEntetesExpressions');
	}
}

function getItemsBlocs(element, indicesBloc, classeRotation, typeBloc) {
	const itemsMenu = [];
	const descTypeBloc = (typeBloc === 'BlocEntetesObjets' ? 'bloc d\'objets' : 'bloc d\'expressions');
	
	// Décalage du bloc
	itemsMenu.push({
		id: 'decalage_bloc',
		libelle: `Décalage du ${descTypeBloc}`,
		icones: [`fa fa-arrow-right-from-bracket ${classeRotation}`],
		multiple: false,
		groupe: 1,
		items: getItemsMenuDecalageBlocTableau(element.elementParent, getBlocEntetesTableau(element, element.elementParent))
	});

	// Ajout de bloc
	itemsMenu.push({
		id: `ajout_avant_bloc`,
		libelle: `Ajouter un ${descTypeBloc} avant`,
		icones: [`fa fa-arrow-left icone-exposant  ${classeRotation}`, `fa fa-table-columns ${classeRotation ? 'rotation-gauche' : ''}`, 'fa fa-plus icone-indice'],
		multiple: false,
		groupe: 2,
		commande: () => {
			let indices = indicesBloc.slice();
			ajouterBlocEntetes(element.elementParent, typeBloc, indices);
		}
	});
	itemsMenu.push({
		id: `ajout_apres_bloc`,
		libelle: `Ajouter un ${descTypeBloc} après`,
		icones: [`fa fa-arrow-right icone-exposant  ${classeRotation}`, `fa fa-table-columns ${classeRotation ? 'rotation-gauche' : ''}`, 'fa fa-plus icone-indice'],
		multiple: false,
		groupe: 2,
		commande: () => {
			let indices = indicesBloc.slice();
			indices[indices.length - 1]++;
			ajouterBlocEntetes(element.elementParent, typeBloc, indices);
		}
	});

	// Suppression du bloc
	itemsMenu.push({
		id: 'suppr_bloc',
		libelle: `Supprimer le ${descTypeBloc}`,
		icones: ['fa fa-trash'],
		style: 'color: var(--couleur-supprimer); ',
		multiple: false,
		groupe: 3,
		commande: () => {
			supprimerBlocEntetes(element.elementParent, typeBloc, indicesBloc);
		}
	});
	return itemsMenu;
}

function getItemsEntetesVariable(element, dansBlocEntetesObjets) {
	if (['EnteteObjetsBloc', 'EnteteObjet', 'EnteteRegroupement', 'EnteteValeurGroupe', 'EnteteExpression', 'ValeurExpression', 'EnteteVariable', 'EnteteValeurVariable'].includes(element.type)) {
		const itemsMenu = [];
		const classeRotation = !dansBlocEntetesObjets ? 'rotation-droite' : '';
		
		// Ajout de variable
		itemsMenu.push({
			id: `ajout_avant_variable`,
			libelle: 'Ajouter une variable avant',
			icones: [`fa fa-arrow-left icone-exposant  ${classeRotation}`, 'fa fa-ruler-horizontal', 'fa fa-plus icone-indice'],
			multiple: false,
			groupe: 1,
			commande: getCommandeAjoutEnteteVariable(element, true)
		});
		itemsMenu.push({
			id: `ajout_apres_variable`,
			libelle: 'Ajouter une variable après',
			icones: [`fa fa-arrow-right icone-exposant  ${classeRotation}`, 'fa fa-ruler-horizontal', 'fa fa-plus icone-indice'],
			multiple: false,
			groupe: 1,
			commande: getCommandeAjoutEnteteVariable(element, false)
		});

		return itemsMenu;
	}
}

function getItemsEntetesRegroupement(element, dansBlocEntetesObjets) {
	const itemsMenu = [];
	const classeRotation = !dansBlocEntetesObjets ? 'rotation-droite' : '';
	
	const itemAffichageSousTotaux = getItemAffichageSousTotaux(element);
	if (itemAffichageSousTotaux) {
		itemsMenu.push(itemAffichageSousTotaux);
	}

	if (['EnteteRegroupement', 'EnteteValeurGroupe', 'EnteteObjetsBloc', 'EnteteObjet'].includes(element.type)) {
		itemsMenu.push({
			id: `ajout_avant_entete_regroupement`,
			libelle: 'Ajouter un regroupement avant',
			icones: [`fa fa-arrow-left icone-exposant  ${classeRotation}`, 'fa fa-object-group', 'fa fa-plus icone-indice'],
			multiple: false,
			groupe: 2,
			commande: getCommandeAjoutEnteteRegroupement(element, true)
		});
	}
	if (['EnteteRegroupement', 'EnteteValeurGroupe'].includes(element.type)) {
		itemsMenu.push({
			id: `ajout_apres_entete_regroupement`,
			libelle: 'Ajouter un regroupement après',
			icones: [`fa fa-arrow-right icone-exposant  ${classeRotation}`, 'fa fa-object-group', 'fa fa-plus icone-indice'],
			multiple: false,
			groupe: 2,
			commande: getCommandeAjoutEnteteRegroupement(element, false)
		});
	}
	return itemsMenu;
}

function getCommandeAjoutEnteteVariable(element, avant) {
	return () => {
		var indices;
		switch(element.type) {
			case 'EnteteObjetsBloc':
			case 'EnteteObjet':
			case 'EnteteRegroupement':
			case 'EnteteValeurGroupe':
				indices = element.position.slice();
				indices.length = 2;
				indices[1] = getBlocEntetesTableau(element, element.elementParent).positionEntetePrincipal;
				if (!avant) {
					indices[1]++;
				}
				break;
			case 'EnteteVariable':
			case 'EnteteValeurVariable':
				indices = element.position.slice();
				if (!avant) {
					indices[indices.length - 1]++;
				}
				if (indices[indices.length - 1] >= getBlocEntetesTableau(element, element.elementParent).positionEntetePrincipal) {
					indices[indices.length - 1]++;
				}
				break;
			case 'EnteteExpression':
			case 'ValeurExpression':
				indices = element.position.slice();
				indices[2] = getBlocEntetesTableau(element, element.elementParent).positionEntetePrincipal;
				if (!avant) {
					indices[2]++;
				}
				break;
		}
		ajouterEnteteVariable(element.elementParent, 'EnteteVariable', indices);
	};
}

function getCommandeAjoutEnteteRegroupement(element, avant) {
	return () => {
		let indices = null;
		switch(element.type) {
			case 'EnteteObjetsBloc':
			case 'EnteteObjet':
				let indiceBlocEntetes = element.position[0];
				let nbEntetesRegroupement = element.elementParent.blocsEntetesObjets[indiceBlocEntetes].entetesRegroupements.length;
				indices = [indiceBlocEntetes, nbEntetesRegroupement];
				break;
			case 'EnteteRegroupement':
			case 'EnteteValeurGroupe':
				indices = element.position.slice();
				break;
		}
		if (!avant) {
			indices[1]++;
		}
		ajouterEnteteRegroupement(element.elementParent, indices);
	};
}

function getItemFiltres(element) {
	const blocEntetesObjets = getBlocEntetesObjetsTableau(element, element.elementParent);
	if (!blocEntetesObjets || !blocEntetesObjets.filtrable) {
		return null;
	}
	const itemsSousMenu = [];
	// Items d'ajout / suppression du filtre au tableau
	var libelleBasculeFiltre = null;
	var iconeBasculeFiltre = null;
	var typeModification = null;
	if (!blocEntetesObjets.filtre) {
		libelleBasculeFiltre = 'Ajouter un filtre au tableau';
		iconeBasculeFiltre = 'fa fa-plus';
		typeModification = 'AJOUT';
	} else {
		libelleBasculeFiltre = 'Retirer le filtre du tableau';
		iconeBasculeFiltre = 'fa fa-trash';
		typeModification = 'SUPPRESSION';
	}
	itemsSousMenu.push({
		id: 'bascule_filtre',
		libelle: libelleBasculeFiltre,
		icones: [iconeBasculeFiltre],
		groupe: 1,
		multiple: false,
		commande: () => {
			editerFiltreBlocEntetes(element.elementParent, blocEntetesObjets, typeModification);
		}
	});
	
	// Items de filtre / d'exclusion d'une valeur
	if (element.type === 'ValeurExpression' && element.valeur && 'valBrute' in element.valeur) {
		const positionValeur = (element.elementParent.expressionsEnColonnes ? element.colonne : element.ligne) - 1;
		const valeurBrute = element.valeur.valBrute;
		itemsSousMenu.push({
			id: 'filtre_valeur',
			libelle: 'Filtrer sur cette valeur',
			icones: ['fa fa-filter'],
			multiple: false,
			commande: () => {
				filtrerValeur(element.elementParent, blocEntetesObjets, 'FILTRE_VALEURS', element.position, positionValeur, [valeurBrute]);
			}
		});
		itemsSousMenu.push({
			id: 'exclusion_valeur',
			libelle: 'Exclure cette valeur',
			icones: ['fa fa-eye-slash'],
			multiple: false,
			commande: () => {
				filtrerValeur(element.elementParent, blocEntetesObjets, 'EXCLUSION_VALEURS', element.position, positionValeur, [valeurBrute]);
			}
		});
	}

	return {
		libelle: 'Filtres',
		icones: ['fa fa-filter'],
		groupe: 2,
		items: itemsSousMenu
	};
}

function getItemAffichageTotaux(element) {
	const blocEntetesObjets = getBlocEntetesObjetsTableau(element, element.elementParent);
	if (blocEntetesObjets != null) {
		const affichageActuel = blocEntetesObjets.enteteObjetsBloc.affichageTotaux;
		const libelleAction = (affichageActuel ? 'Masquer les valeurs totales' : 'Afficher les valeurs totales');
		return {
			id: 'afficher_totaux',
			libelle: libelleAction,
			icones: ['icone-custom icone-sigma', 'fa fa-table icone-exposant'],
			groupe: 2,
			multiple: true,
			commande: () => {
				basculerAffichageTotauxBlocEntetes(element.elementParent, element.position);
			}
		};
	} else {
		return null;
	}
}

function getItemAffichageSousTotaux(element) {
	const enteteRegroupement = getEnteteRegroupementTableau(element, element.elementParent);
	if (enteteRegroupement != null) {
		const affichageActuel = enteteRegroupement.affichageSousTotaux;
		const libelleAction = (affichageActuel ? 'Masquer les sous-totaux' : 'Afficher les sous-totaux');
		return {
			id: 'afficher_sous_totaux',
			libelle: libelleAction,
			icones: ['icone-custom icone-sigma', 'fa fa-object-group icone-exposant'],
			groupe: 1,
			multiple: true,
			commande: () => {
				basculerAffichageSousTotauxRegroupement(getPositionBriqueRapport(element, element.elementParent));
			}
		};
	} else {
		return null;
	}
}

function getItemsMenuMiseEnForme(misesEnForme) {
	const items = [];
	items.push({
		id: 'modif_mise_en_forme',
		libelle: 'Modifier la mise en forme...',
		icones: ['fa fa-wand-magic-sparkles'],
		groupe: 1,
		multiple: true,
		commande: () => {affichageModaleEditionMiseEnForme.value = true;}
	});
	if (misesEnForme != null) {
		for (const miseEnForme of misesEnForme) {
			items.push({
				id: miseEnForme,
				class: 'mise-en-forme-proposee',
				libelle: 'Texte',
				style: miseEnForme,
				groupe: 2,
				multiple: true,
				commande: () => {
					modifierMisesEnForme({miseEnForme: miseEnForme})
				}
			});
		}
	}
	return items;
}

function getItemsMenuLargeur(element) {
	const items = [];
	if (element.type != 'GRAPHIQUE') {
		items.push({
			id: 'largeur_ajustee',
			libelle: 'Ajustée au contenu',
			icones: ['fa fa-arrows-to-circle'],
			multiple: true,
			commande: () => modifierLargeurElement(element, 'MIN')
		});
	}
	items.push({
		id: 'largeur_max',
		libelle: 'Maximale',
		icones: ['fa fa-maximize'],
		multiple: true,
		commande: () => modifierLargeurElement(element, 'MAX')
	});
	return items;
}

function getItemsMenuAlignement(element) {
	const items = [];
	items.push({
		libelle: 'Aligner au début',
		icones: ['fa fa-align-left'],
		commande: () => modifierAlignementElement(element, 'DEBUT')
	});
	items.push({
		libelle: 'Aligner au centre',
		icones: ['fa fa-align-center'],
		commande: () => modifierAlignementElement(element, 'CENTRE')
	});
	items.push({
		libelle: 'Aligner à la fin',
		icones: ['fa fa-align-right'],
		commande: () => modifierAlignementElement(element, 'FIN')
	});
	return items;
}

function getItemsMenuDecalageElementRapport(element) {
	const decalagesProposes = [0, 5, 10, 20, 50, 100];
	if (element.decalage && decalagesProposes.indexOf(element.decalage) === -1) {
		decalagesProposes.push(element.decalage);
		decalagesProposes.sort(function(a, b) {return a - b;});
	}
	const items = decalagesProposes.map(decalage => ({
				libelle: `${decalage} pixels`,
				disabled: decalage === element.decalage,
				commande: () => {
					modifierDecalageElement(element, decalage);
				}
			}));
	items.push({
		libelle: 'Autre...',
		icones: ['fa fa-ellipsis'],
		commande: () => {
			affichageModaleSaisieDecalage.value = true;
		}
	});
	return items;	
}

function getItemsMenuDecalageBlocTableau(tableau, blocEntetes) {
	const decalagesProposes = [0, 5, 10, 20, 50, 100];
	if (decalagesProposes.indexOf(blocEntetes.decalage) === -1) {
		decalagesProposes.push(blocEntetes.decalage);
		decalagesProposes.sort(function(a, b) {return a - b;});
	}
	const items = decalagesProposes.map(decalage => ({
				libelle: `${decalage} pixels`,
				disabled: decalage === blocEntetes.decalage,
				commande: () => {
					modifierDecalageBlocTableau(tableau, blocEntetes, decalage);
				}
			}));
	items.push({
		libelle: 'Autre...',
		commande: () => {
			affichageModaleSaisieDecalage.value = true;
		}
	});
	return items;	
}

function activerModeEdition() {
	if (elementsSelectionnes.value.length > 0) {
		elementsSelectionnes.value[0].modeEdition = true;
	}
}

function ajouterElementRapport(positionSection, positionNouvelElement, typeElement) {
	const positionSelection = positionNouvelElement.slice();
	appelerApiModifModele(apiAjouterElementRapport, positionSection, positionNouvelElement, typeElement, (reponseOK) => {
		if (reponseOK) {
			const elementEtParent = trouverElementEtParent(positionSection, positionSelection);
			if (elementEtParent != null) {
				selectionnerElement(elementEtParent.element, elementEtParent.parent);
				if (typeElement === 'SELECTEUR') {
					editerExpression();
				}
			}
		}
	});
}

function modifierMasquageBriquesRapport() {
	const positionsBriques = elementsSelectionnes.value.map(elt => getPositionBriqueRapport(elt, elt.elementParent));
	appelerApiModifModele(apiModifierMasquageBriquesRapport, positionsBriques);
}

function modifierMisesEnForme(infosMiseEnForme) {
    const cartographieMisesEnForme = {};
	for (const elt of elementsSelectionnes.value) {
		let cle = JSON.stringify(getPositionBriqueRapport(elt, elt.elementParent));
		if (!Object.keys(cartographieMisesEnForme).includes(cle)) {
			cartographieMisesEnForme[cle] = {elements: []};
            cartographieMisesEnForme[cle].miseEnForme = getMiseEnFormeElement(elt);
        }
		cartographieMisesEnForme[cle].elements.push(elt);
	}
    const misesEnFormeElements = [];
    for (const cleMiseEnForme of Object.keys(cartographieMisesEnForme)) {
		const miseEnFormeIni = cartographieMisesEnForme[cleMiseEnForme].miseEnForme;
		let nouvelleMiseEnFormeElement = null;
		if (infosMiseEnForme.transformation != null) {
			nouvelleMiseEnFormeElement = infosMiseEnForme.transformation(miseEnFormeIni, infosMiseEnForme.miseEnForme);
		} else {
			nouvelleMiseEnFormeElement = infosMiseEnForme.miseEnForme;
		}
		nouvelleMiseEnFormeElement.type = miseEnFormeIni.type;
        const premierElement = cartographieMisesEnForme[cleMiseEnForme].elements[0];
        misesEnFormeElements.push({
            positionBrique: getPositionBriqueRapport(premierElement, premierElement.elementParent),
            miseEnForme: nouvelleMiseEnFormeElement
        });
    }
	appelerApiModifModele(apiMettreEnFormeElements, misesEnFormeElements, (reponseOK) => {
		if (reponseOK) {
			affichageModaleEditionMiseEnForme.value = false;
		}
	});
}

function modifierLargeurElement(element, modeLargeur) {
	appelerApiModifModele(apiModifierLargeurElementRapport, element, modeLargeur);
}

function modifierDecalageElement(element, decalage) {
	if (element.decalage != decalage) {
		appelerApiModifModele(apiModifierDecalageElementRapport, element, decalage, (reponseOK) => {
			if (reponseOK) {
				affichageModaleSaisieDecalage.value = false;
			}
		});
	}
}

function modifierAlignementElement(element, modeAlignement) {
	appelerApiModifModele(apiModifierAlignementElementRapport, element, modeAlignement);
}

function transposerElement(element) {
	appelerApiModifModele(apiTransposerElementsRapport, element);
}

function ajouterEnteteExpression(tableau, indices) {
	appelerApiModifModele(apiAjouterElementTableau, tableau, 'EnteteExpression', indices, '', (reponseOK) => {
		if (reponseOK) {
			const celluleEnteteExpression = trouverPremiereCelluleTableau(tableau, 'ValeurExpression', indices);
			if (celluleEnteteExpression != null) {
				selectionnerElement(celluleEnteteExpression, tableau);
				editerExpression();
			}
		}
	});
}

function ajouterEnteteRegroupement(tableau, indices) {
	appelerApiModifModele(apiAjouterElementTableau, tableau, 'EnteteRegroupement', indices, '', (reponseOK) => {
		if (reponseOK) {
			const celluleEnteteRegroupement = trouverPremiereCelluleTableau(tableau, 'EnteteValeurGroupe', indices);
			if (celluleEnteteRegroupement != null) {
				selectionnerElement(celluleEnteteRegroupement, tableau);
				editerExpression();
			}
		}
	});
}

function ajouterEnteteVariable(tableau, typeEnteteVariable, indices) {
	appelerApiModifModele(apiAjouterElementTableau, tableau, typeEnteteVariable, indices, '', (reponseOK) => {
		if (reponseOK) {
			const celluleEnteteVariable = trouverPremiereCelluleTableau(tableau, typeEnteteVariable, indices);
			if (celluleEnteteVariable != null) {
				selectionnerElement(celluleEnteteVariable, tableau);
				editerExpression();
			}
		}
	});
}

function ajouterBlocEntetes(tableau, typeBloc, indices) {
	appelerApiModifModele(apiAjouterElementTableau, tableau, typeBloc, indices, '');
}

function supprimerBlocEntetes(tableau, typeBloc, indices) {
	appelerApiModifModele(apiSupprimerBlocEntetesTableau, tableau, typeBloc, indices);
}

function editerFiltreBlocEntetes(tableau, blocEntetes, typeModification) {
	appelerApiModifModele(apiEditerFiltreBlocEntetesTableau, tableau, blocEntetes, typeModification, [], -1, []);
}

function filtrerValeur(tableau, blocEntetes, typeModification, positionEnteteExpression, positionValeur, valeurs) {
	appelerApiModifModele(apiEditerFiltreBlocEntetesTableau, tableau, blocEntetes, typeModification, positionEnteteExpression, positionValeur, valeurs);
}

function basculerAffichageTotauxBlocEntetes(tableau, indices) {
	appelerApiModifModele(apiBasculerAffichageTotauxBlocEntetesTableau, tableau, 'BlocEntetesObjets', indices);
}

function basculerAffichageSousTotauxRegroupement(positionBriqueRapport) {
	appelerApiModifModele(apiBasculerAffichageSousTotauxRegroupementTableau, positionBriqueRapport);
}

function modifierDecalageBlocTableau(tableau, blocEntetes, decalage) {
	if (blocEntetes.decalage != decalage) {
		appelerApiModifModele(apiModifierDecalageBlocEntetesTableau, tableau, blocEntetes, decalage, (reponseOK) => {
			if (reponseOK) {
				affichageModaleSaisieDecalage.value = false;
			}
		});
	}
}

function modifierTypeGraphique(graphique, typeGraphique) {
	appelerApiModifModele(apiModifierTypeGraphique, graphique, typeGraphique);
}

function insererSerie(graphique, indice) {
	appelerApiModifModele(apiAjouterSerieGraphique, graphique, indice, (reponseOK) => {
		if (reponseOK) {
			selectionnerElement(graphique.series[indice], graphique);
			editerExpression();
		}
	});
}

function ajouterObjet(typeObjet, textesValeursCles) {
	const tableau = (element.elementParent.type === 'TABLEAU' ? element.elementParent : null);
	appelerApiModifModele(apiAjouterObjet, typeObjet, textesValeursCles, (reponseOK) => {
		if (reponseOK) {
			if (tableau == null) {
				return;
			}
			const celluleObjet = trouverPremiereCelluleTableauVerifiant(tableau, c => {
				return c.type === 'EnteteObjet' && c.valeur?.valBrute === textesValeursCles[0];
			});
			if (celluleObjet != null) {
				selectionnerElement(celluleObjet, tableau);
			}
		}
	});
}

</script>

<template>
	<div>
		<button class="bouton btn-actions fa-solid fa-caret-down" 
			ref="boutonActions"
			@mousedown.stop="basculerAffichageBarreFlottante" />
		<ModaleEditionMiseEnForme 
			v-model:visible="affichageModaleEditionMiseEnForme" 
			:elements="elementsSelectionnes"
			@modifMiseEnForme="(infosMiseEnForme) => modifierMisesEnForme(infosMiseEnForme)" />
		<ModaleEditionDecalage 
			v-model:visible="affichageModaleSaisieDecalage" 
			:elements="elementsSelectionnes"
			@modifDecalageBlocTableau="modifierDecalageBlocTableau"
			@modifDecalageElementRapport="modifierDecalageElement" />
		<ModaleAjoutObjet 
			v-if="affichageModaleAjoutObjet"
			v-model:visible="affichageModaleAjoutObjet" 
			:typeObjet="getTypeObjet(getBlocEntetesTableau(element, element.elementParent)?.idTypeObjetAjoutable)"
			@ajoutObjet="(objet) => ajouterObjet(objet.typeObjet, objet.textesValeursCles)" />
	</div>
</template>

<style scoped>

</style>